if (!customElements.get('product-section')) {
  customElements.define(
    'product-section',
    class ProductSection extends HTMLElement {
      constructor() {
        super();

        this.quantity = 1;
        this.activeGalleryIndex = 0;
        this.pendingVariantId = null;
        this.shippingTimerInterval = null;

        this.handleClick = this.handleClick.bind(this);
        this.handleGalleryKeydown = this.handleGalleryKeydown.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleCartUpdated = this.handleCartUpdated.bind(this);
        this.handleCartError = this.handleCartError.bind(this);
        this.updateShippingTimer = this.updateShippingTimer.bind(this);
      }

      connectedCallback() {
        this.galleryDialog = this.querySelector('[data-gallery-dialog]');
        this.galleryZoomStage = this.querySelector('[data-gallery-zoom-stage]');
        this.galleryZoomItems = this.querySelectorAll('[data-gallery-zoom-item]');
        this.shippingTimer = this.querySelector('[data-shipping-timer]');

        this.addEventListener('click', this.handleClick);
        this.galleryDialog?.addEventListener('close', this.handleDialogClose);
        document.addEventListener('cart:updated', this.handleCartUpdated);
        document.addEventListener('cart:error', this.handleCartError);

        this.updateShippingTimer();
        this.shippingTimerInterval = window.setInterval(this.updateShippingTimer, 30000);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.galleryDialog?.removeEventListener('close', this.handleDialogClose);
        document.removeEventListener('keydown', this.handleGalleryKeydown);
        document.removeEventListener('cart:updated', this.handleCartUpdated);
        document.removeEventListener('cart:error', this.handleCartError);

        if (this.shippingTimerInterval) {
          window.clearInterval(this.shippingTimerInterval);
          this.shippingTimerInterval = null;
        }
      }

      handleClick(event) {
        const galleryOpen = event.target.closest('[data-gallery-open]');
        const galleryClose = event.target.closest('[data-gallery-close]');
        const galleryPrev = event.target.closest('[data-gallery-prev]');
        const galleryNext = event.target.closest('[data-gallery-next]');
        const decreaseBtn = event.target.closest('[data-qty-decrease]');
        const increaseBtn = event.target.closest('[data-qty-increase]');
        const addToCartBtn = event.target.closest('[data-add-to-cart]');

        if (galleryOpen && this.contains(galleryOpen)) {
          event.preventDefault();
          this.openGallery(Number.parseInt(galleryOpen.dataset.galleryIndex || '0', 10));
          return;
        }

        if (galleryClose && this.contains(galleryClose)) {
          event.preventDefault();
          this.closeGallery();
          return;
        }

        if (galleryPrev && this.contains(galleryPrev)) {
          event.preventDefault();
          this.showGalleryImage(this.activeGalleryIndex - 1);
          return;
        }

        if (galleryNext && this.contains(galleryNext)) {
          event.preventDefault();
          this.showGalleryImage(this.activeGalleryIndex + 1);
          return;
        }

        if (decreaseBtn && this.contains(decreaseBtn)) {
          event.preventDefault();
          this.setQuantity(this.quantity - 1);
          return;
        }

        if (increaseBtn && this.contains(increaseBtn)) {
          event.preventDefault();
          this.setQuantity(this.quantity + 1);
          return;
        }

        if (addToCartBtn && this.contains(addToCartBtn)) {
          event.preventDefault();
          this.addToCart(addToCartBtn);
        }
      }

      setQuantity(nextQuantity) {
        const quantity = Math.max(1, nextQuantity);
        this.quantity = quantity;

        const qtyValue = this.querySelector('[data-qty-value]');
        const decreaseBtn = this.querySelector('[data-qty-decrease]');

        if (qtyValue) {
          qtyValue.textContent = String(quantity);
        }

        if (decreaseBtn) {
          decreaseBtn.disabled = quantity <= 1;
        }
      }

      addToCart(button) {
        const variantId = Number.parseInt(button.dataset.variantId || '', 10);

        if (!variantId || button.disabled || button.hasAttribute('data-add-to-cart-locked')) return;

        const quantity = button.closest('.product-form') ? this.quantity : 1;

        this.pendingVariantId = String(variantId);
        button.disabled = true;

        document.dispatchEvent(
          new CustomEvent('cart:add', {
            bubbles: true,
            detail: { variantId, quantity, openDrawer: true },
          }),
        );
      }

      handleCartUpdated() {
        if (!this.pendingVariantId) return;

        const button = this.querySelector(
          `[data-add-to-cart][data-variant-id="${this.pendingVariantId}"]`,
        );

        if (button && !button.hasAttribute('data-add-to-cart-locked')) {
          button.disabled = false;
        }

        this.pendingVariantId = null;
      }

      handleCartError() {
        this.handleCartUpdated();
      }

      openGallery(index) {
        if (!this.galleryDialog) return;

        this.showGalleryImage(index);
        this.galleryDialog.showModal();
        document.addEventListener('keydown', this.handleGalleryKeydown);
      }

      closeGallery() {
        if (!this.galleryDialog?.open) return;

        this.galleryDialog.close();
      }

      handleDialogClose() {
        document.removeEventListener('keydown', this.handleGalleryKeydown);
      }

      handleGalleryKeydown(event) {
        if (!this.galleryDialog?.open) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeGallery();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showGalleryImage(this.activeGalleryIndex - 1);
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showGalleryImage(this.activeGalleryIndex + 1);
        }
      }

      showGalleryImage(index) {
        if (!this.galleryZoomItems?.length) return;

        const total = this.galleryZoomItems.length;
        const normalizedIndex = ((index % total) + total) % total;

        this.activeGalleryIndex = normalizedIndex;

        for (const image of this.galleryZoomItems) {
          const itemIndex = Number.parseInt(image.dataset.galleryIndex || '0', 10);
          image.hidden = itemIndex !== normalizedIndex;
        }
      }

      updateShippingTimer() {
        this.shippingTimer = this.querySelector('[data-shipping-timer]');

        if (!this.shippingTimer) return;

        const formBlock = this.shippingTimer.closest('.product-form');
        const cutoffTime = formBlock?.dataset.cutoffTime || '14:00';
        const todayTemplate = this.shippingTimer.dataset.shippingTimerToday || '';
        const tomorrowTemplate = this.shippingTimer.dataset.shippingTimerTomorrow || '';
        const textEl = this.shippingTimer.querySelector('[data-shipping-timer-text]');

        if (!textEl || !todayTemplate || !tomorrowTemplate) {
          this.shippingTimer.hidden = true;
          return;
        }

        const remaining = this.getRemainingUntilCutoff(cutoffTime);

        if (!remaining) {
          this.shippingTimer.hidden = true;
          return;
        }

        const template = remaining.isTomorrow ? tomorrowTemplate : todayTemplate;
        const message = template
          .replaceAll('[[hours]]', String(remaining.hours))
          .replaceAll('[[minutes]]', String(remaining.minutes));

        textEl.textContent = message;
        this.shippingTimer.hidden = false;
      }

      getRemainingUntilCutoff(cutoffTime) {
        const match = /^(\d{1,2}):(\d{2})$/.exec(cutoffTime.trim());

        if (!match) return null;

        const hours = Number.parseInt(match[1], 10);
        const minutes = Number.parseInt(match[2], 10);

        if (hours > 23 || minutes > 59) return null;

        const now = new Date();
        const cutoff = new Date(now);

        cutoff.setHours(hours, minutes, 0, 0);

        const isTomorrow = now >= cutoff;

        if (isTomorrow) {
          cutoff.setDate(cutoff.getDate() + 1);
        }

        const diffMs = cutoff.getTime() - now.getTime();
        const totalMinutes = Math.floor(diffMs / 60000);

        if (totalMinutes <= 0) return null;

        return {
          hours: Math.floor(totalMinutes / 60),
          minutes: totalMinutes % 60,
          isTomorrow,
        };
      }
    },
  );
}
